import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import {
  ReviewSource,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useDelayedWorkplaceReviewFlag } from "@src/appV2/redesign/Workplace/Review/useDelayedWorkplaceReviewFlag";

import { Reaction } from "../types";
import { WorkplaceReviewCommentCompactCard } from "./CompactCard";

interface WorkplaceReviewCommentCompactCardProps {
  comment: WorkplaceCommentType;
  workplaceId: string;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onClick?: () => void;
  showEngagementActions?: boolean;
  isReadOnly?: boolean;
}

export function WorkplaceReviewCommentCompactCardContainer(
  props: WorkplaceReviewCommentCompactCardProps
) {
  const { comment, workplaceId, onLikeChanged, onClick, showEngagementActions, isReadOnly } = props;
  const {
    createdAt,
    workerInfo,
    text,
    type: commentType,
    workerReaction,
    aggregates,
    shiftPeriod,
    shiftWorkerQualification,
    patientsPerNurse,
    reviewTag,
    rating,
    media,
    source,
    externalDetails,
  } = comment.attributes;
  const { id: workerId, name: workerName } = workerInfo;

  const isExternalReview = isDefined(source) && source !== ReviewSource.CBH;

  const { useDelayedReviewUI } = useDelayedWorkplaceReviewFlag();

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerId), {
      enabled: isDefined(workerId) && !isExternalReview,
    });

  const profilePictureUrl = isExternalReview
    ? externalDetails?.reviewerPhotoUrl
    : profilePicture?.distribution_url;

  const displayDateIso =
    isExternalReview && isDefined(externalDetails?.publishedAt)
      ? externalDetails.publishedAt
      : createdAt;

  return (
    <WorkplaceReviewCommentCompactCard
      commentId={comment.id}
      commentText={text}
      commentType={commentType === "WRITTEN" || commentType === "VIDEO" ? commentType : undefined}
      mediaUrl={media?.[0]?.url}
      createdAtIso={displayDateIso}
      totalLikes={aggregates.totalLikes}
      workerName={workerName}
      isLiked={workerReaction === Reaction.LIKE}
      profilePictureUrl={profilePictureUrl}
      isLoadingProfilePicture={isExternalReview ? false : isProfilePictureLoading}
      workplaceId={workplaceId}
      maxLines={8}
      shiftPeriod={shiftPeriod}
      patientsPerNurse={patientsPerNurse}
      reviewTag={reviewTag}
      shiftWorkerQualification={shiftWorkerQualification}
      rating={rating}
      useDelayedReviewLayout={useDelayedReviewUI}
      showEngagementActions={showEngagementActions}
      isReadOnly={isReadOnly}
      source={source}
      reviewUrl={externalDetails?.reviewUrl}
      onClick={onClick}
      onLikeChanged={onLikeChanged}
    />
  );
}
